import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiDelete, apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api/client';
import { emailChannelEndpoints } from '@/lib/api/endpoints';
import { messagingKeys } from '@/lib/queryKeys';

/** Mirrors the `EmailIgnoreRuleField` Prisma enum. */
export type EmailIgnoreRuleField = 'FROM_ADDRESS' | 'FROM_DOMAIN' | 'SUBJECT' | 'BODY';

/** Mirrors the `EmailIgnoreRuleMatch` Prisma enum. */
export type EmailIgnoreRuleMatch = 'EQUALS' | 'CONTAINS' | 'STARTS_WITH' | 'ENDS_WITH' | 'REGEX';

export interface EmailIgnoreRule {
  id: string;
  field: EmailIgnoreRuleField;
  match: EmailIgnoreRuleMatch;
  value: string;
  enabled: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
}

/**
 * The API runs with `forbidNonWhitelisted`, so these payloads must carry
 * exactly the keys the DTOs declare — an extra key is a 400.
 */
export interface CreateEmailIgnoreRulePayload {
  field: EmailIgnoreRuleField;
  match: EmailIgnoreRuleMatch;
  value: string;
  enabled?: boolean;
}

export type UpdateEmailIgnoreRulePayload = Partial<CreateEmailIgnoreRulePayload>;

export const ignoreRulesKey = ['email', 'ignore-rules'] as const;

/**
 * Every inbound-email ignore rule for the tenant. Cached so the Settings page
 * and the inbox thread header (which checks whether the current sender is
 * already ignored) share one fetch.
 */
export function useEmailIgnoreRules() {
  return useQuery({
    queryKey: ignoreRulesKey,
    queryFn: () =>
      apiGet<{ rules: EmailIgnoreRule[] }>(
        operationsClient,
        emailChannelEndpoints.ignoreRules(),
      ).then((r) => r.rules),
  });
}

/**
 * Invalidate the rule list plus the thread list — a rule change alters which
 * inbound email survives ingestion, so the inbox view is stale afterwards.
 */
function useRuleInvalidation() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ignoreRulesKey });
    void qc.invalidateQueries({ queryKey: messagingKeys.threads() });
  };
}

export function useCreateEmailIgnoreRule() {
  const invalidate = useRuleInvalidation();
  return useMutation<EmailIgnoreRule, Error, CreateEmailIgnoreRulePayload>({
    mutationFn: (payload) =>
      apiPost<EmailIgnoreRule, CreateEmailIgnoreRulePayload>(
        operationsClient,
        emailChannelEndpoints.ignoreRules(),
        payload,
      ),
    onSuccess: () => {
      invalidate();
      toast.success('Ignore rule added.');
    },
    // Surface the server's message: it explains *why* (duplicate rule, bad
    // regex, "@stripe.com" in a domain rule) far better than a generic string.
    onError: (err) => toast.error(err.message || 'Failed to add rule. Please try again.'),
  });
}

export function useUpdateEmailIgnoreRule() {
  const invalidate = useRuleInvalidation();
  return useMutation<EmailIgnoreRule, Error, { id: string; patch: UpdateEmailIgnoreRulePayload }>({
    mutationFn: ({ id, patch }) =>
      apiPatch<EmailIgnoreRule, UpdateEmailIgnoreRulePayload>(
        operationsClient,
        emailChannelEndpoints.ignoreRule(id),
        patch,
      ),
    onSuccess: () => invalidate(),
    onError: (err) => toast.error(err.message || 'Failed to update rule. Please try again.'),
  });
}

export function useDeleteEmailIgnoreRule() {
  const invalidate = useRuleInvalidation();
  return useMutation<{ removed: boolean }, Error, string>({
    mutationFn: (id) =>
      apiDelete<{ removed: boolean }>(operationsClient, emailChannelEndpoints.ignoreRule(id)),
    onSuccess: () => {
      invalidate();
      toast.success('Ignore rule removed.');
    },
    onError: () => toast.error('Failed to remove rule. Please try again.'),
  });
}

/**
 * One-click "Ignore sender" from an inbox email thread. Idempotent on the
 * server, so double-clicking is safe.
 */
export function useBlockEmailSender() {
  const invalidate = useRuleInvalidation();
  return useMutation<EmailIgnoreRule, Error, string>({
    mutationFn: (email) =>
      apiPost<EmailIgnoreRule, { email: string }>(
        operationsClient,
        emailChannelEndpoints.blockSender(),
        { email },
      ),
    onSuccess: () => {
      invalidate();
      toast.success('Sender ignored.');
    },
    onError: (err) => toast.error(err.message || 'Failed to ignore sender. Please try again.'),
  });
}

/** Field labels — kept in sync with `describeRule` on the backend. */
export const FIELD_LABELS: Record<EmailIgnoreRuleField, string> = {
  FROM_ADDRESS: 'Sender address',
  FROM_DOMAIN: 'Sender domain',
  SUBJECT: 'Subject',
  BODY: 'Message body',
};

/** Operator labels — kept in sync with `describeRule` on the backend. */
export const MATCH_LABELS: Record<EmailIgnoreRuleMatch, string> = {
  EQUALS: 'is',
  CONTAINS: 'contains',
  STARTS_WITH: 'starts with',
  ENDS_WITH: 'ends with',
  REGEX: 'matches regex',
};

/** Human-readable one-liner, mirroring the backend's `describeRule`. */
export function describeIgnoreRule(rule: {
  field: EmailIgnoreRuleField;
  match: EmailIgnoreRuleMatch;
  value: string;
}): string {
  return `${FIELD_LABELS[rule.field]} ${MATCH_LABELS[rule.match]} "${rule.value}"`;
}

/**
 * Is this exact address already covered by a one-click sender block? Only
 * checks FROM_ADDRESS/EQUALS rules — deliberately NOT a full rule evaluation,
 * because the inbox button's job is toggling that one rule, and claiming
 * "already ignored" for a message a domain rule happens to cover would leave
 * the operator with no way to act on the sender.
 */
export function isSenderBlocked(rules: readonly EmailIgnoreRule[], email: string): boolean {
  const normalized = email.trim().toLowerCase();
  if (!normalized) return false;
  return rules.some(
    (r) =>
      r.enabled && r.field === 'FROM_ADDRESS' && r.match === 'EQUALS' && r.value === normalized,
  );
}
